import React, { useState, useEffect, useCallback } from 'react';
// @ts-ignore - lucide-react types incompatible with TS 4.9.5
import { Save, GripVertical, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { clientDashboardService } from '../../services/clientDashboard';

interface TabConfig {
  key: string;
  label: string;
  visible: boolean;
}

const DEFAULT_TABS: TabConfig[] = [
  { key: 'consolidated', label: 'Consolidado', visible: true },
  { key: 'main', label: 'Visão Geral', visible: true },
  { key: 'performance', label: 'Performance', visible: true },
  { key: 'orders', label: 'Pedidos', visible: true },
  { key: 'analytics', label: 'Analytics', visible: true },
];

interface Preferencias {
  tabOrder?: TabConfig[];
  defaultPeriodo?: string;
  chartAnimations?: boolean;
  compactMode?: boolean;
}

const ClientDashboardConfig: React.FC = () => {
  const [, setPreferencias] = useState<Preferencias>({});
  const [tabOrder, setTabOrder] = useState<TabConfig[]>(DEFAULT_TABS);
  const [defaultPeriodo, setDefaultPeriodo] = useState('hoje');
  const [chartAnimations, setChartAnimations] = useState(true);
  const [compactMode, setCompactMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const fetchPreferencias = useCallback(async () => {
    try {
      const res = await clientDashboardService.getPreferencias();
      const prefs = res.data || {};
      setPreferencias(prefs);
      if (prefs.tabOrder && Array.isArray(prefs.tabOrder)) {
        // Mesclar com abas padrão para garantir que novas abas apareçam
        const merged = prefs.tabOrder.map((t: TabConfig) => {
          const def = DEFAULT_TABS.find(d => d.key === t.key);
          return def ? { ...def, ...t } : t;
        });
        // Adicionar abas novas que não existem nas preferências
        DEFAULT_TABS.forEach(d => {
          if (!merged.find((m: TabConfig) => m.key === d.key)) {
            merged.push(d);
          }
        });
        setTabOrder(merged);
      }
      if (prefs.defaultPeriodo) setDefaultPeriodo(prefs.defaultPeriodo);
      if (prefs.chartAnimations !== undefined) setChartAnimations(prefs.chartAnimations);
      if (prefs.compactMode !== undefined) setCompactMode(prefs.compactMode);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setError('Erro ao carregar preferências.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferencias();
  }, [fetchPreferencias]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await clientDashboardService.updatePreferencias({
        tabOrder,
        defaultPeriodo,
        chartAnimations,
        compactMode
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao salvar preferências.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setTabOrder(DEFAULT_TABS);
    setDefaultPeriodo('hoje');
    setChartAnimations(true);
    setCompactMode(false);
  };

  const toggleTabVisibility = (index: number) => {
    const updated = [...tabOrder];
    // Não permitir esconder a aba consolidada
    if (updated[index].key === 'consolidated') return;
    updated[index] = { ...updated[index], visible: !updated[index].visible };
    setTabOrder(updated);
  };

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    const updated = [...tabOrder];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(index, 0, moved);
    setTabOrder(updated);
    setDragIndex(index);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  if (loading) {
    return (
      <div className="client-dashboard-loading">
        <div className="client-dashboard-spinner" />
        Carregando configurações...
      </div>
    );
  }

  return (
    <>
      <h2 className="client-dashboard-section-title">Configurações do Dashboard</h2>

      {error && (
        <div className="client-dashboard-error-message" style={{ marginBottom: 16 }}>{error}</div>
      )}
      {saved && (
        <div style={{
          background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
          borderRadius: 8, padding: '12px 16px', color: '#4ade80', fontSize: 14,
          marginBottom: 16, textAlign: 'center'
        }}>
          Preferências salvas com sucesso!
        </div>
      )}

      {/* Ordem das abas */}
      <div className="client-dashboard-config-card">
        <h3 className="client-dashboard-chart-title">Ordem e Visibilidade das Abas</h3>
        <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>
          Arraste para reordenar as abas do dashboard. Clique no ícone de olho para mostrar ou ocultar.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {tabOrder.map((tab, index) => (
            <div
              key={tab.key}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`client-dashboard-config-tab-item ${dragIndex === index ? 'dragging' : ''}`}
            >
              <GripVertical size={16} className="client-dashboard-config-grip" />
              <span className="client-dashboard-config-tab-pos">{index + 1}</span>
              <span style={{ flex: 1, fontSize: 14, color: tab.visible ? '#e2e8f0' : '#475569' }}>
                {tab.label}
              </span>
              {tab.key === 'consolidated' ? (
                <span style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>sempre visível</span>
              ) : (
                <button
                  onClick={() => toggleTabVisibility(index)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: tab.visible ? '#4ade80' : '#475569', padding: 4
                  }}
                  title={tab.visible ? 'Ocultar aba' : 'Mostrar aba'}
                >
                  {tab.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Período padrão */}
      <div className="client-dashboard-config-card">
        <h3 className="client-dashboard-chart-title">Período Padrão</h3>
        <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>
          Defina o período padrão ao abrir o dashboard.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { key: 'hoje', label: 'Hoje' },
            { key: 'semana', label: 'Semana' },
            { key: 'mes', label: 'Mês' }
          ].map(p => (
            <button
              key={p.key}
              className={`client-dashboard-filter-btn ${defaultPeriodo === p.key ? 'active' : ''}`}
              onClick={() => setDefaultPeriodo(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Opções de exibição */}
      <div className="client-dashboard-config-card">
        <h3 className="client-dashboard-chart-title">Opções de Exibição</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
          <label className="client-dashboard-config-toggle">
            <span>Animações dos gráficos</span>
            <input
              type="checkbox"
              checked={chartAnimations}
              onChange={e => setChartAnimations(e.target.checked)}
            />
            <span className="client-dashboard-config-toggle-slider" />
          </label>
          <label className="client-dashboard-config-toggle">
            <span>Modo compacto (tabelas com menos espaçamento)</span>
            <input
              type="checkbox"
              checked={compactMode}
              onChange={e => setCompactMode(e.target.checked)}
            />
            <span className="client-dashboard-config-toggle-slider" />
          </label>
        </div>
      </div>

      {/* Botões */}
      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        <button
          className="client-dashboard-btn client-dashboard-btn-primary"
          onClick={handleSave}
          disabled={saving}
          style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'center' }}
        >
          <Save size={16} />
          {saving ? 'Salvando...' : 'Salvar Preferências'}
        </button>
        <button
          className="client-dashboard-btn"
          onClick={handleReset}
          style={{
            background: 'rgba(148,163,184,0.1)', color: '#94a3b8',
            border: '1px solid rgba(148,163,184,0.2)',
            display: 'flex', alignItems: 'center', gap: 8
          }}
        >
          <RotateCcw size={16} />
          Restaurar Padrões
        </button>
      </div>
    </>
  );
};

export default ClientDashboardConfig;
